// TeamActivityTable.jsx

import { useState } from "react";
import Modal from "react-modal";

Modal.setAppElement("#root");

const TeamActivityTable = ({ entries }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [localScreenshots, setLocalScreenshots] = useState({});
  const [capturingId, setCapturingId] = useState(null);

  const handleCapture = async (userId, timeEntryId, status) => {
    if (status === "Offline") {
      alert("Member is offline, cannot take screenshot");
      return;
    }

    setCapturingId(timeEntryId);

    try {
      const res = await fetch(
        "https://time-tracker-gx97.onrender.com/api/screenshots/request",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, time_entry_id: timeEntryId }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Screenshot request blocked");
        return;
      }

      // Success notification
      alert("Screenshot request sent successfully!");

      if (data.file_url) {
        setLocalScreenshots(prev => ({ ...prev, [timeEntryId]: data.file_url }));
      }
    } catch (err) {
      alert("Failed to send capture request");
    } finally {
      setCapturingId(null);
    }
  };

  const openModal = (url) => {
    setSelectedImage(url);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  // Format time for display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Get status color based on status string
  const getStatusColor = (status) => {
    switch (status) {
      case "Working":
        return "#10b981"; // Green
      case "Break":
        return "#f59e0b"; // Amber
      case "Idle":
        return "#6b7280"; // Gray
      case "Offline":
        return "#9ca3af"; // Light gray
      default:
        return "#9ca3af";
    }
  };

  // Filter out offline members
  const activeEntries = entries.filter(entry => entry.status !== "Offline");

  return (
    <>
      <div className="team-activity-container">
        {/* Show message if no active members */}
        {activeEntries.length === 0 ? (
          <div style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text-secondary)",
            backgroundColor: "var(--card-bg)",
            borderRadius: "8px"
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: "16px", opacity: 0.5 }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <h4 style={{ margin: "0 0 8px", color: "var(--text-color)" }}>No Active Team Members</h4>
            <p style={{ margin: 0, fontSize: "0.9rem" }}>All team members are currently offline</p>
          </div>
        ) : (
          <table className="activity-table">
            <thead>
              <tr>
                <th>Team Member</th>
                <th>Activity</th>
                <th>Time</th>
                <th>Screenshot</th>
              </tr>
            </thead>
            <tbody>
              {activeEntries.map((entry) => {
                const latestScreenshot = localScreenshots[entry.id] || entry.file_url;
                const isOffline = entry.status === "Offline";
                const isCapturing = capturingId === entry.id;

                return (
                  <tr key={entry.id} className="activity-row">
                    {/* Team Member Column */}
                    <td>
                      <div className="member-info">
                        <div className="member-name-container">
                          <span 
                            className="status-dot" 
                            style={{ backgroundColor: getStatusColor(entry.status) }}
                          ></span>
                          <span className="member-name">{entry.name}</span>
                        </div>
                        <div className="member-status" style={{ color: getStatusColor(entry.status) }}>
                          {entry.status}
                        </div>
                      </div>
                    </td>

                    {/* Activity Column */}
                    <td>
                      <div className="activity-info">
                        <div className="app-title">{entry.last_active_window}</div>
                        <div className="window-title" title={entry.last_window_title}>
                          {entry.last_window_title}
                        </div>
                      </div>
                    </td>

                    {/* Time Column */}
                    <td>
                      <div className="time-display">
                        {formatTime(entry.duration || 0)}
                      </div>
                    </td>

                    {/* Screenshot Column */}
                    <td>
                      <div className="screenshot-container">
                        {/* Capture Button - Always visible */}
                        <button
                          className={`capture-btn ${isOffline ? 'disabled' : ''} ${isCapturing ? 'capturing' : ''}`}
                          onClick={() => handleCapture(entry.user_id, entry.id, entry.status)}
                          disabled={isOffline || isCapturing}
                          title={isOffline ? "Member is offline" : "Capture screenshot"}
                        >
                          {isCapturing ? (
                            <>
                              <span className="spinner"></span>
                              <span>Capturing...</span>
                            </>
                          ) : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                              </svg>
                              <span>Capture</span>
                            </>
                          )}
                        </button>

                        {/* Screenshot Preview */}
                        <div className="screenshot-preview-container">
                          {latestScreenshot ? (
                            <div className="screenshot-preview" onClick={() => openModal(latestScreenshot)}>
                              <img
                                src={latestScreenshot}
                                alt="Screenshot preview"
                                className="screenshot-thumbnail"
                              />
                              <div className="preview-label">Preview</div>
                            </div>
                          ) : (
                            <div className="screenshot-placeholder">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        contentLabel="Screenshot Preview"
        className="screenshot-modal"
        overlayClassName="modal-overlay"
      >
        <div className="modal-header">
          <h3>Screenshot Preview</h3>
          <button className="close-btn" onClick={closeModal}>×</button>
        </div>
        <div className="modal-content">
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full screenshot"
              className="full-screenshot"
            />
          )}
        </div>
      </Modal>

      {/* Add CSS styles */}
      <style jsx>{`
        .team-activity-container {
          overflow-x: auto;
          background: var(--card-bg);
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .activity-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 800px;
        }

        .activity-table thead {
          background: var(--header-bg);
        }

        .activity-table th {
          padding: 14px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.85rem;
          color: var(--text-secondary);
          border-bottom: 1px solid var(--border-color);
        }

        .activity-row {
          border-bottom: 1px solid var(--border-color);
        }

        .activity-row:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }

        .activity-row td {
          padding: 12px 16px;
          vertical-align: middle;
        }

        /* Team Member Column */
        .member-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .member-name-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .member-name {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text-color);
        }

        .member-status {
          font-size: 0.75rem;
          margin-left: 16px;
          font-weight: 500;
        }

        /* Activity Column */
        .activity-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          max-width: 300px;
        }

        .app-title {
          font-weight: 500;
          color: var(--text-color);
          font-size: 0.9rem;
        }

        .window-title {
          color: var(--text-secondary);
          font-size: 0.8rem;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Time Column */
        .time-display {
          font-weight: 600;
          color: var(--text-color);
          font-size: 0.9rem;
        }

        /* Screenshot Column */
        .screenshot-container {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 60px;
        }

        .capture-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 100px;
          height: 32px;
        }

        .capture-btn:hover:not(.disabled):not(.capturing) {
          background: linear-gradient(135deg, #3b82f6, #60a5fa);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .capture-btn:active:not(.disabled):not(.capturing) {
          transform: translateY(0);
        }

        .capture-btn.disabled {
          background: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .capture-btn.capturing {
          background: #6b7280;
          cursor: wait;
        }

        .spinner {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .screenshot-preview-container {
          display: flex;
          align-items: center;
        }

        .screenshot-preview {
          position: relative;
          cursor: pointer;
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid var(--border-color);
          transition: all 0.2s;
          width: 120px;
          height: 68px;
        }

        .screenshot-preview:hover {
          border-color: #3b82f6;
          transform: scale(1.02);
        }

        .screenshot-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .preview-label {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          font-size: 0.7rem;
          text-align: center;
          padding: 2px 4px;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .screenshot-preview:hover .preview-label {
          opacity: 1;
        }

        .screenshot-placeholder {
          width: 120px;
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px dashed var(--border-color);
          border-radius: 4px;
          color: var(--text-secondary);
          background: rgba(0, 0, 0, 0.02);
        }

        /* Modal Styles */
        .screenshot-modal {
          max-width: 90vw;
          max-height: 90vh;
          margin: auto;
          background: var(--card-bg);
          border-radius: 8px;
          padding: 0;
          outline: none;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-overlay {
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid var(--border-color);
        }

        .modal-header h3 {
          margin: 0;
          color: var(--text-color);
          font-size: 1rem;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          line-height: 1;
        }

        .close-btn:hover {
          background: rgba(0, 0, 0, 0.05);
          color: var(--text-color);
        }

        .modal-content {
          padding: 16px;
          max-height: calc(90vh - 60px);
          overflow: auto;
        }

        .full-screenshot {
          width: 100%;
          height: auto;
          border-radius: 4px;
        }
      `}</style>
    </>
  );
};

export default TeamActivityTable;